import { Box, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import type { AdminAuditEvent, AdminPage } from '@/admin/adminApi';
import { Avatar, Button, Card, EmptyState } from '@/ui/kit';

import { actionLabel } from '@/admin/audit/auditPresentation';

import { actorLabel, entryPath, eventTime, targetLabel } from './dashboardModel';
import { ReadBlock } from './ReadBlock';
import type { AdminRead } from './useAdminRead';

/**
 * « Dernières actions » : cinq actions sensibles du journal d'audit (les simples
 * consultations, `admin.access`, sont ecartees a la lecture), sous 600 px les deux
 * plus recentes.
 *
 * L'acteur est nomme par son **adresse**, jamais par son UUID ; la console, qui
 * n'a pas de compte, s'ecrit « Console ». La cible n'est ecrite que si elle se
 * lit (adresse, clef de reglage) : un identifiant technique ne dit rien ici.
 */

const NARROW_ROWS = 2;

const Row = ({ event, now, narrow }: { readonly event: AdminAuditEvent; readonly now: Date; readonly narrow: boolean }): ReactElement => {
  const target = narrow ? null : targetLabel(event);

  return (
    <Box
      component="li"
      sx={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: 1.25, py: 1.25, borderBottom: 1, borderColor: 'tokens.border' }}
    >
      <Avatar name={actorLabel(event)} tone="blue" size={28} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="p" sx={{ fontSize: 13, lineHeight: '18px', overflowWrap: 'anywhere' }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {actorLabel(event)}
          </Box>
          <Box component="span" sx={{ color: 'tokens.textMuted' }}>
            {` · ${actionLabel(event.action)}`}
          </Box>
        </Typography>
        {target === null ? null : (
          <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted', fontFamily: 'ui-monospace, monospace', overflowWrap: 'anywhere' }}>
            {target}
          </Typography>
        )}
      </Box>
      <Typography component="time" dateTime={event.occurredAt} sx={{ fontSize: 12, color: 'tokens.textMuted', flexShrink: 0 }}>
        {eventTime(event.occurredAt, now)}
      </Typography>
    </Box>
  );
};

export const RecentActionsCard = ({
  events,
  narrow,
  onRetry,
}: {
  readonly events: AdminRead<AdminPage<AdminAuditEvent>>;
  readonly narrow: boolean;
  readonly onRetry: () => void;
}): ReactElement => {
  const journalPath = entryPath('audit');

  return (
    <Card
      title="Dernières actions"
      subtitle="Journal d’audit"
      actions={
        journalPath === null ? undefined : (
          <Button variant="ghost" size="S" to={journalPath}>
            Journal
          </Button>
        )
      }
    >
      <ReadBlock state={events} label="Dernières actions" onRetry={onRetry} skeletonHeight={narrow ? 100 : 200}>
        {({ items }, at) =>
          items.length === 0 ? (
            <EmptyState title="Aucune action sensible" description="Le journal ne contient encore aucune action autre qu’une consultation." />
          ) : (
            <Box component="ul" sx={{ m: 0, p: 0 }}>
              {(narrow ? items.slice(0, NARROW_ROWS) : items).map((event) => (
                <Row key={event.id} event={event} now={at} narrow={narrow} />
              ))}
            </Box>
          )
        }
      </ReadBlock>
    </Card>
  );
};
